/**
 * Hook for managing canvas image loading and auto-import functionality.
 * Uses global store for canvas images to share state across components.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore, getAppStore } from '@/store';
import {
  generateCanvasImage,
  type CanvasImage,
} from '@/utils/canvas/canvasUtils';
import uploadAndUpdateInput from '@/utils/canvas/uploadAndUpdateInput';
import {
  getLoadedFlowId,
  setLoadedFlowId,
} from '@/utils/canvas/canvasLoadState';
import { InputType } from '@/types';

import type { IAppStore } from '@/store';

interface UseCanvasImagesOptions {
  /** Flow ID that triggers canvas loading when changed */
  flowId: string | undefined;
}

interface UseCanvasImagesReturn {
  /** Available canvas images (from global store) */
  images: CanvasImage[];
  /** Whether canvas selector modal is open */
  isOpen: boolean;
  /** Open selector for a specific input */
  openSelector: (inputId: string) => void;
  /** Close selector */
  closeSelector: () => void;
  /** Handle image selection from modal */
  handleSelect: (image: CanvasImage) => Promise<void>;
}

const selector = (store: IAppStore) => ({
  canvasImages: store.canvasImages,
  setCanvasImages: store.setCanvasImages,
  updateInput: store.updateInput,
});

const useCanvasImages = ({
  flowId,
}: UseCanvasImagesOptions): UseCanvasImagesReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingInputId, setPendingInputId] = useState<string | null>(null);
  const hasLoaded = useRef<string | null>(null);

  // Use global store for canvas images
  const { canvasImages, setCanvasImages, updateInput } = useAppStore(selector);

  // Load canvas images and auto-import single image
  useEffect(() => {
    if (!flowId) return;
    // Skip if this flowId was already loaded (prevents React Strict Mode double-load)
    if (hasLoaded.current === flowId || getLoadedFlowId() === flowId) return;

    hasLoaded.current = flowId;
    setLoadedFlowId(flowId);

    const loadImages = async () => {
      const { inputs } = getAppStore();
      const hasMediaInputs = inputs.some(
        (input) =>
          input.type === InputType.IMAGE || input.type === InputType.VIDEO
      );
      if (!hasMediaInputs) return;

      try {
        // Generate canvas image if canvas has layers
        const canvasImage = await generateCanvasImage();

        if (canvasImage) {
          // Auto-import canvas image to first empty media input
          const latestInputs = getAppStore().inputs;
          const emptyInput = latestInputs.find(
            (input) =>
              (input.type === InputType.IMAGE ||
                input.type === InputType.VIDEO) &&
              !input.src
          );

          if (emptyInput) {
            const storeUpdateInput = getAppStore().updateInput;
            await uploadAndUpdateInput({
              inputId: emptyInput.id,
              image: canvasImage,
              updateInput: storeUpdateInput,
            });
          }

          // Also add to available images for manual selection
          setCanvasImages([canvasImage]);
        }
      } catch {
        // Silent fail - canvas images are optional
      }
    };

    loadImages();
  }, [flowId, setCanvasImages]);

  const openSelector = useCallback(
    async (inputId: string) => {
      if (canvasImages.length === 0) return;

      // Import directly — generateCanvasImage produces a single flattened image
      await uploadAndUpdateInput({
        inputId,
        image: canvasImages[0],
        updateInput,
      });
    },
    [canvasImages, updateInput]
  );

  const closeSelector = useCallback(() => {
    setIsOpen(false);
    setPendingInputId(null);
  }, []);

  const handleSelect = useCallback(
    async (image: CanvasImage) => {
      if (!pendingInputId) return;

      const inputId = pendingInputId;
      closeSelector();

      await uploadAndUpdateInput({
        inputId,
        image,
        updateInput,
      });
    },
    [pendingInputId, updateInput, closeSelector]
  );

  return {
    images: canvasImages,
    isOpen,
    openSelector,
    closeSelector,
    handleSelect,
  };
};

export default useCanvasImages;
